import { useCallback, useEffect, useRef, useState } from 'react';
import { cloudFunctionV2, cloudFunctionV2Auth } from '../functions';
import { CRM_PROVIDERS } from '../utils/crmProviderConfigs';

// Fetches the pipeline / stage options for whichever CRM is connected, off the
// provider's `fetchPipelinesEnv` endpoint. Extracted from CrmActionForm so the
// self-purchase modal can reuse the same fetch instead of re-deriving the
// per-provider response shapes (they differ: GHL nests stages under each
// pipeline, FUB returns a flat pipelines array, Salesforce returns stages only).
//
// Returns:
//   pipelines      — [{ id, name, stages: [{ id, name }] }] (GHL/FUB; [] for SF)
//   stages         — [{ ... }] Salesforce stages ([] for GHL/FUB)
//   selectorsState — 'loading' | 'ready' | 'error'
//   ghlTimezone    — the GHL sub-account's IANA timezone, or null
//   refetch        — re-run the fetch (for a Retry affordance)
export function useCrmSelectors(companyId, provider) {
    const [pipelines, setPipelines] = useState([]);
    const [stages, setStages] = useState([]);
    const [selectorsState, setSelectorsState] = useState('loading');
    const [ghlTimezone, setGhlTimezone] = useState(null);

    // Ref-based cancel so a provider switch or retry can't race a stale response
    // into state.
    const fetchTokenRef = useRef(0);

    // Reset on provider switch — a pipeline ID from the previous CRM is meaningless.
    useEffect(() => {
        setPipelines([]);
        setStages([]);
        setSelectorsState('loading');
        setGhlTimezone(null);
    }, [provider]);

    const fetchSelectors = useCallback(async () => {
        const config = provider ? CRM_PROVIDERS[provider] : null;
        if (!config || !companyId) return;
        const url = process.env[config.fetchPipelinesEnv];
        if (!url) { setSelectorsState('error'); return; }

        const token = ++fetchTokenRef.current;
        setSelectorsState('loading');

        try {
            // GHL endpoints require an authenticated session (companyId derived
            // server-side); FUB/SF stages endpoints are unchanged.
            const callFn = provider === 'ghl' ? cloudFunctionV2Auth : cloudFunctionV2;
            const res = await callFn(url, { companyId });
            if (token !== fetchTokenRef.current) return;
            if (res?.status !== 200 || !res.data) {
                setSelectorsState('error');
                return;
            }

            if (provider === 'ghl') {
                const tz = res.data.timezone || res.data.data?.timezone || null;
                if (tz) setGhlTimezone(tz);
                const data = res.data.pipelines || res.data.data?.pipelines || res.data;
                setPipelines(Array.isArray(data) ? data.map((p) => ({
                    id: p.id,
                    name: p.name || 'Unnamed Pipeline',
                    stages: (Array.isArray(p.stages) ? p.stages : []).map((s) => ({
                        id: s.id,
                        name: s.name || 'Unnamed Stage',
                    })),
                })) : []);
            } else if (provider === 'fub') {
                setPipelines(Array.isArray(res.data.pipelines) ? res.data.pipelines : []);
            } else if (provider === 'salesforce') {
                setStages(Array.isArray(res.data.stages) ? res.data.stages : []);
            }
            setSelectorsState('ready');
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error(`Error fetching ${provider} selectors:`, err);
            if (token === fetchTokenRef.current) setSelectorsState('error');
        }
    }, [companyId, provider]);

    useEffect(() => {
        fetchSelectors();
        // Bump the token on cleanup so any still-in-flight response sees a
        // mismatch and skips its setState.
        return () => { fetchTokenRef.current += 1; };
    }, [fetchSelectors]);

    return { pipelines, stages, selectorsState, ghlTimezone, refetch: fetchSelectors };
}

export default useCrmSelectors;
